import { ExpandedLocalDetails } from "../../samples/@types/expanded_local_details";
import { formatDateUTC } from "../../samples/util";
import { EMPTY_VALUE } from "../consts";
import {
  columnDiffers,
  MatrixColumnName,
  MatrixValues,
  DetectionCounts,
  matrixValuesFromDetails,
  matrixValuesFromDetections,
  referenceCellText
} from "../differenceMatrixFields";
import { diffSets, enabledCapabilities } from "../utils";
import {
  CompareRow,
  CompareScalarRow,
  CompareSetRow,
  CompareSide
} from "./@types";
import { COMPARE_GROUPS } from "./consts";

const asText = (value?: string | number | null): string => {
  if (value === undefined || value === null || value === "") {
    return EMPTY_VALUE;
  }
  return String(value);
};

const asDate = (value?: string): string => asText(formatDateUTC(value));

const scalar = (
  group: string,
  label: string,
  main: string,
  other: string,
  isMonospace = false,
  differs = main !== other
): CompareScalarRow => ({
  kind: "scalar",
  group,
  label,
  main,
  other,
  isMonospace,
  differs
});

/** The grid's rule for "differs", so the modal and the grid cannot disagree. */
const matrixRow = (
  group: string,
  label: string,
  column: MatrixColumnName,
  main: MatrixValues,
  other: MatrixValues
): CompareScalarRow =>
  scalar(
    group,
    label,
    referenceCellText(column, main),
    referenceCellText(column, other),
    false,
    columnDiffers(column, main, other)
  );

const set = (
  group: string,
  label: string,
  main: string[],
  other: string[]
): CompareSetRow => {
  const diff = diffSets(main, other);
  return {
    kind: "set",
    group,
    label,
    ...diff,
    differs: diff.onlyMain + diff.onlyOther > 0
  };
};

/** Both sides come from the same endpoint, so this compares like for like. */
export const buildCompareRows = (
  main: ExpandedLocalDetails,
  other: ExpandedLocalDetails,
  detections: { main?: DetectionCounts; other?: DetectionCounts } = {}
): CompareRow[] => {
  const a = main.sample;
  const b = other.sample;
  const mainValues = {
    ...matrixValuesFromDetails(main),
    ...matrixValuesFromDetections(detections.main)
  };
  const otherValues = {
    ...matrixValuesFromDetails(other),
    ...matrixValuesFromDetections(detections.other)
  };
  const { FILE, CLASSIFICATION, PROVENANCE, TAGS } = COMPARE_GROUPS;

  return [
    matrixRow(FILE, "File type", "file_type", mainValues, otherValues),
    scalar(
      FILE,
      "Identification",
      asText(a.tc_format_identification),
      asText(b.tc_format_identification)
    ),
    matrixRow(FILE, "File size", "file_size", mainValues, otherValues),
    matrixRow(
      FILE,
      "Extracted files",
      "extracted_files",
      mainValues,
      otherValues
    ),
    scalar(FILE, "MD5", asText(a.md5), asText(b.md5), true),
    scalar(FILE, "SHA-256", asText(a.sha256), asText(b.sha256), true),
    scalar(
      CLASSIFICATION,
      "Classification",
      asText(a.classification),
      asText(b.classification)
    ),
    matrixRow(
      CLASSIFICATION,
      "Threat name",
      "threat_name",
      mainValues,
      otherValues
    ),
    scalar(
      CLASSIFICATION,
      "Classified by",
      asText(a.mapped_classification_reason?.title),
      asText(b.mapped_classification_reason?.title)
    ),
    matrixRow(
      CLASSIFICATION,
      "AV detections",
      "av_detections",
      mainValues,
      otherValues
    ),
    matrixRow(
      CLASSIFICATION,
      "Certificate validation",
      "cert_validation",
      mainValues,
      otherValues
    ),
    scalar(
      PROVENANCE,
      "First seen (cloud)",
      asDate(a.ticloud?.first_seen),
      asDate(b.ticloud?.first_seen)
    ),
    scalar(
      PROVENANCE,
      "Last seen (cloud)",
      asDate(a.ticloud?.last_seen),
      asDate(b.ticloud?.last_seen)
    ),
    scalar(
      PROVENANCE,
      "First seen (local)",
      asDate(a.local_first_seen),
      asDate(b.local_first_seen)
    ),
    scalar(
      PROVENANCE,
      "Sources",
      asText(a.sources?.ticloud_sources?.count),
      asText(b.sources?.ticloud_sources?.count)
    ),
    scalar(
      PROVENANCE,
      "Comments",
      asText(a.comments?.count),
      asText(b.comments?.count)
    ),
    set(TAGS, "User tags", a.tags?.user_tags, b.tags?.user_tags),
    set(TAGS, "System tags", a.tags?.ticore_tags, b.tags?.ticore_tags),
    set(TAGS, "Capabilities", enabledCapabilities(a), enabledCapabilities(b))
  ];
};

export const countDifferences = (rows: CompareRow[]): number =>
  rows.filter(row => row.differs).length;

export const uniqueGlyph = (side: CompareSide): string =>
  side === "main" ? "−" : "+";
